import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {ROUTES} from '../../app.routes';
import {SearchForm} from '../../sdk/component/search/searchForm';
import {AuthenticationService} from '../../sdk/service/authentication.service';
import {SearchService} from '../../sdk/component/search/search.service';
import {
    Account,
    AccountsSortField,
    AccountState,
    AccountType,
    WebAccountService,
    WebQRService
} from 'web-angular-client';
import {AccountStatusUtil} from '../../sdk/interface/accountStatusUtil';
import {AlertService} from '../../sdk/service/alert.service';
import {saveAs} from 'file-saver';
import {NgxUiLoaderService} from 'ngx-ui-loader';
import {ConfirmationService} from '../../sdk/service/confirmation.service';
import {SubscriptionService} from '../../sdk/service/subscription.service';
import {finalize} from 'rxjs';
import {EnvironmentService} from '../../sdk/service/environment.service';

@Component({
    selector: 'app-qr',
    templateUrl: './qr.dashboard.component.html',
    styleUrls: ['./qr.dashboard.component.scss'],
    standalone: false
})
export class QrDashboardComponent extends SearchForm implements OnInit
{

    sortFields: SortField[];
    selectedSortField: SortField | null;
    protected readonly AccountType = AccountType;

    constructor(
        protected router: Router,
        protected authenticationService: AuthenticationService,
        protected environmentService: EnvironmentService,
        protected webQrService: WebQRService,
        protected searchService: SearchService,
        protected accountStatusUtil: AccountStatusUtil,
        private alertService: AlertService,
        private ngxLoader: NgxUiLoaderService,
        private confirmationService: ConfirmationService,
        private webAccountService: WebAccountService,
        private subscriptionService: SubscriptionService)
    {
        super(authenticationService, webQrService, searchService, environmentService);

        this.sortFields = [
            {id: 'description', name: 'Description'},
            {id: 'dateTimeCreated', name: 'Date Created'},
            {id: 'minimumAmount', name: 'Minimum Amount'},
            {id: 'maximumAmount', name: 'Maximum Amount'}
        ];
    }

    get contents(): [Account]
    {
        return this.results?.content;
    }

    get canAddQrCode(): boolean
    {
        return this.hasRouteAccess(ROUTES.SECURE.QR.CREATE) && !this.isGuestAdminUser() && this.isSubscriptionValid();
    }

    get getTooltipText(): string
    {
        if (this.isGuestAdminUser())
        {
            return 'Guest accounts cannot create QR codes.';
        }
        else if (!this.isSubscriptionValid)
        {
            return 'Please pay your subscription fee before attempting to create a QR code.';
        }
        else if (!this.hasRouteAccess(ROUTES.SECURE.QR.CREATE))
        {
            return 'This feature is not enabled';
        }
        return '';
    }

    public create()
    {
        throw new Error('Method not implemented.');
    }

    public view(qrCode: any)
    {
        this.router.navigate([ROUTES.SECURE.QR.DETAILS.pathSansParameters, qrCode.id]);
    }

    getDefaultSortField(): string
    {
        return AccountsSortField.ID;
    }

    public getAccountStatusUtil(): AccountStatusUtil
    {
        return this.accountStatusUtil;
    }

    public isResultListEmpty(): boolean
    {
        return (this.results != null && this.results.content != null && this.results.content.length > 0);
    }

    addQrCode(): void
    {
        this.router.navigate([ROUTES.SECURE.QR.CREATE.path]).then();
    }

    getMinimumAmount(qrCode: Account): number
    {
        return qrCode.minimumAmount;
    }

    getMaximumAmount(qrCode: Account): number
    {
        return qrCode.maximumAmount;
    }

    getQrStatusDescription(): string
    {
        return 'ACTIVE: QR code can accept payments \n' +
            'CANCELLED: QR code has been cancelled & cannot accept payments';
    }

    getAccountStatus(qrCode: Account): string
    {
        return qrCode.accountState === AccountState.PAYMENT_OUTSTANDING ?
            //TODO: Check for Cancelled due to admin
            'ACTIVE' : (qrCode.accountState === AccountState.PAYMENT_CANCELLED)
                ? 'CANCELLED' : '';
    }

    editQr(qrCode: Account)
    {
        if (this.isGuestAdminUser())
        {
            this.displayGuestPrivilegeError();
            return;
        }

        this.router.navigate([ROUTES.SECURE.QR.EDIT.pathSansParameters, qrCode.id]).then();
    }

    downloadQrCode(qrCode: Account): void
    {
        this.ngxLoader.start();
        this.webQrService.downloadQr(qrCode.id).pipe(finalize(() => this.ngxLoader.stop())).subscribe(
            (blob: Blob) =>
            {
                saveAs(blob, `${qrCode.description}.pdf`);
            },
            (error) =>
            {
                this.alertService.error('Failed to download the QR code, please contact support for assistance.');
            });
    }

    cancel(qrCode: Account): void
    {
        if (this.isGuestAdminUser())
        {
            this.displayGuestPrivilegeError();
            return;
        }

        this.confirmationService.confirm(
            'Cancel QR Code',
            `Are you sure you want to cancel QR code [${qrCode.description}] ?`)
            .then((shouldCancel: Boolean) =>
            {
                if (shouldCancel)
                {
                    this.ngxLoader.start();
                    this.webAccountService.cancelAccount(qrCode.id).subscribe({
                        next: (qrCode: Account) =>
                        {
                            this.ngxLoader.stop();
                            this.router.navigate([ROUTES.SECURE.QR.DETAILS.pathSansParameters, qrCode.id]);
                        }, error: () =>
                        {
                            this.ngxLoader.stop();
                            this.alertService.error('Failed to cancel QR code', false);
                        }
                    });
                }
            });
    }

    private displayGuestPrivilegeError(): void
    {
        this.alertService.error('Guest accounts cannot perform this action.', false);
    }

    private isSubscriptionValid(): boolean
    {
        return this.subscriptionService.isSubscriptionValid;
    }
}

interface SortField
{
    id: string;
    name: string;
}